import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';

interface Template {
  id: string;
  code: string;
  name: string;
  qualitative_enabled: boolean;
}
interface RatingGroup {
  id: string;
  code: string;
  name: string;
}
interface Factor {
  name: string;
  factorType: string;
  ratingGroupId: string;
  scorePercent: string;
}
interface Evaluation {
  code: string;
  name: string;
  scorePercent: string;
  factors: Factor[];
}

const EMPTY_TEMPLATE = { code: '', name: '', qualitativeEnabled: true };

export default function AppraisalTemplateBuilder() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [ratingGroups, setRatingGroups] = useState<RatingGroup[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_TEMPLATE);
  const [error, setError] = useState('');

  const [editing, setEditing] = useState<Template | null>(null);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [qualitativeItems, setQualitativeItems] = useState<string[]>([]);

  const load = async () => {
    const [t, rg] = await Promise.all([api.get('/pas/masters/templates'), api.get('/pas/masters/rating-groups')]);
    setTemplates(t.data.templates || []);
    setRatingGroups(rg.data.ratingGroups || []);
  };

  useEffect(() => {
    load();
  }, []);

  const createTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/pas/masters/templates', form);
      setShowForm(false);
      setForm(EMPTY_TEMPLATE);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create');
    }
  };

  const openBuilder = async (t: Template) => {
    setEditing(t);
    setError('');
    const res = await api.get(`/pas/masters/templates/${t.id}`);
    setEvaluations(
      (res.data.evaluations || []).map((e: any) => ({
        code: e.code,
        name: e.name,
        scorePercent: String(e.score_percent),
        factors: (e.factors || []).map((f: any) => ({
          name: f.name,
          factorType: f.factor_type,
          ratingGroupId: f.rating_group_id || '',
          scorePercent: String(f.score_percent),
        })),
      }))
    );
    setQualitativeItems((res.data.qualitativeItems || []).map((q: any) => q.item_text));
  };

  const addEvaluation = () => {
    setEvaluations((prev) => [...prev, { code: '', name: '', scorePercent: '', factors: [] }]);
  };

  const updateEvaluation = (idx: number, patch: Partial<Evaluation>) => {
    setEvaluations((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
  };

  const addFactor = (evalIdx: number) => {
    setEvaluations((prev) =>
      prev.map((e, i) => (i === evalIdx ? { ...e, factors: [...e.factors, { name: '', factorType: 'RATING', ratingGroupId: '', scorePercent: '' }] } : e))
    );
  };

  const updateFactor = (evalIdx: number, factorIdx: number, patch: Partial<Factor>) => {
    setEvaluations((prev) =>
      prev.map((e, i) =>
        i === evalIdx ? { ...e, factors: e.factors.map((f, fi) => (fi === factorIdx ? { ...f, ...patch } : f)) } : e
      )
    );
  };

  const evalTotal = evaluations.reduce((sum, e) => sum + (Number(e.scorePercent) || 0), 0);

  const saveEvaluations = async () => {
    if (!editing) return;
    setError('');
    try {
      await api.put(`/pas/masters/templates/${editing.id}/evaluations`, {
        evaluations: evaluations.map((e) => ({
          code: e.code,
          name: e.name,
          scorePercent: Number(e.scorePercent),
          factors: e.factors.map((f) => ({ name: f.name, factorType: f.factorType, ratingGroupId: f.ratingGroupId || null, scorePercent: Number(f.scorePercent) })),
        })),
      });
      await api.put(`/pas/masters/templates/${editing.id}/qualitative-items`, {
        items: qualitativeItems.map((text, idx) => ({ itemText: text, sortOrder: idx + 1 })),
      });
      alert('Template saved');
      setEditing(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save — check totals equal 100%');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Appraisal Template</h1>
        <Button onClick={() => setShowForm(true)}>+ New Template</Button>
      </div>

      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          { key: 'qualitative_enabled', header: 'Qualitative', render: (r) => (r.qualitative_enabled ? 'Yes' : 'No') },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <Button variant="ghost" onClick={() => openBuilder(row)}>
                Build
              </Button>
            ),
          },
        ]}
        rows={templates}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="New Template">
        <form onSubmit={createTemplate} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.qualitativeEnabled} onChange={(e) => setForm({ ...form, qualitativeEnabled: e.target.checked })} />
            Enable Qualitative Section
          </label>
          <Button type="submit">Create</Button>
        </form>
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={`Build Template — ${editing?.name}`}>
        <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className={`text-sm font-medium ${Math.round(evalTotal * 100) === 10000 ? 'text-green-600' : 'text-red-600'}`}>
            Evaluation total: {evalTotal}% (must equal 100%)
          </div>
          {evaluations.map((ev, evalIdx) => {
            const factorTotal = ev.factors.reduce((s, f) => s + (Number(f.scorePercent) || 0), 0);
            return (
              <div key={evalIdx} className="border rounded p-3">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <Input label="Code" maxLength={6} value={ev.code} onChange={(e) => updateEvaluation(evalIdx, { code: e.target.value })} />
                  <Input label="Name" value={ev.name} onChange={(e) => updateEvaluation(evalIdx, { name: e.target.value })} />
                  <Input label="Score %" type="number" value={ev.scorePercent} onChange={(e) => updateEvaluation(evalIdx, { scorePercent: e.target.value })} />
                </div>
                <div className={`text-xs font-medium mb-2 ${Math.round(factorTotal * 100) === 10000 ? 'text-green-600' : 'text-red-600'}`}>
                  Factor total: {factorTotal}% (must equal 100%)
                </div>
                {ev.factors.map((f, factorIdx) => (
                  <div key={factorIdx} className="grid grid-cols-4 gap-2 mb-1">
                    <Input label="Factor Name" value={f.name} onChange={(e) => updateFactor(evalIdx, factorIdx, { name: e.target.value })} />
                    <Select
                      label="Type"
                      options={[
                        { value: 'YES_NO', label: 'Yes/No' },
                        { value: 'TRUE_FALSE', label: 'True/False' },
                        { value: 'RATING', label: 'Rating' },
                      ]}
                      value={f.factorType}
                      onChange={(e) => updateFactor(evalIdx, factorIdx, { factorType: e.target.value })}
                    />
                    {f.factorType === 'RATING' && (
                      <Select
                        label="Rating Group"
                        options={ratingGroups.map((rg) => ({ value: rg.id, label: rg.name }))}
                        value={f.ratingGroupId}
                        onChange={(e) => updateFactor(evalIdx, factorIdx, { ratingGroupId: e.target.value })}
                      />
                    )}
                    <Input label="Score %" type="number" value={f.scorePercent} onChange={(e) => updateFactor(evalIdx, factorIdx, { scorePercent: e.target.value })} />
                  </div>
                ))}
                <Button variant="secondary" onClick={() => addFactor(evalIdx)}>
                  + Factor
                </Button>
              </div>
            );
          })}
          <Button variant="secondary" onClick={addEvaluation}>
            + Evaluation
          </Button>

          <h4 className="text-sm font-medium text-gray-700 mt-2">Qualitative Items</h4>
          {qualitativeItems.map((text, idx) => (
            <div key={idx} className="flex gap-2">
              <input
                className="flex-1 border rounded px-2 py-1 text-sm"
                value={text}
                onChange={(e) => setQualitativeItems((prev) => prev.map((t, i) => (i === idx ? e.target.value : t)))}
              />
              <button className="text-red-600 text-xs" onClick={() => setQualitativeItems((prev) => prev.filter((_, i) => i !== idx))}>
                Remove
              </button>
            </div>
          ))}
          <Button variant="secondary" onClick={() => setQualitativeItems((prev) => [...prev, ''])}>
            + Qualitative Item
          </Button>

          <Button onClick={saveEvaluations}>Save Template</Button>
        </div>
      </Modal>
    </div>
  );
}
